import isPropValid from '@emotion/is-prop-valid';
import React, { FC, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { StyleSheetManager, WebTarget } from 'styled-components';

import { Layout } from '~/app/Layout';
import { ROUTE as APP_ROUTE } from '~/app/routes';
import { DialogProvider } from '~/components/Dialog/Provider';
import { StepUpDialogHost } from '~/components/StepUpDialogHost';
import { Provider as ColumnAPIProvider } from '~/lib/api';
import { useCodeHighlighting } from '~/lib/json/useCodeHighlighting';
import { initMonitoring } from '~/lib/monitoring';

import { AuthProvider } from './AuthProvider';
import { LDClientIdentifier } from './LDClientIdentifier';
import { PagePlatformInvite } from './pages/PlatformInvite';
import Router from './Router';

const AuthenticatedApp: FC = () => {
  const location = useLocation();
  // Authenticated invitation URLs use /app?invite=<platform_id>; the invitation code is stripped after login.
  const invitedPlatformId =
    location.pathname === APP_ROUTE.ROOT ? new URLSearchParams(location.search).get('invite') : null;

  if (invitedPlatformId) {
    return <PagePlatformInvite platformId={invitedPlatformId} />;
  }

  return (
    <Layout>
      <Router />
      <StepUpDialogHost />
    </Layout>
  );
};

export const App: FC = () => {
  useCodeHighlighting();

  useEffect(initMonitoring, []);

  // Add backwards compatibility from styled-components v5 -> v6:
  // https://styled-components.com/docs/faqs#shouldforwardprop-is-no-longer-provided-by-default
  const shouldForwardProp = useCallback((propName: string, target: WebTarget) => {
    if (typeof target === 'string') {
      return isPropValid(propName);
    }

    return true;
  }, []);

  return (
    <StyleSheetManager shouldForwardProp={shouldForwardProp}>
      <ColumnAPIProvider>
        <AuthProvider>
          <LDClientIdentifier>
            <DialogProvider>
              <AuthenticatedApp />
            </DialogProvider>
          </LDClientIdentifier>
        </AuthProvider>
      </ColumnAPIProvider>
    </StyleSheetManager>
  );
};
